import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { FaExclamationTriangle, FaCalendarAlt, FaFlag, FaClipboardList, FaUser, FaWrench, FaShare, FaDownload, FaFilePdf, FaFile, FaExternalLinkAlt } from 'react-icons/fa';
import { updateIssueStatus, downloadIssueReportPdf, downloadMaintenanceReportPdf, requestQuote } from '../../api/mechanicalApi';

// Helper function to extract date string from date object or string
function getDateString(dateValue) {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') return dateValue;
  if (typeof dateValue === 'object' && dateValue.iso) return dateValue.iso;
  return '';
}

// Helper function to format date for display
function formatDate(dateValue) {
  const dateStr = getDateString(dateValue);
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return dateStr;
  }
}

// Helper to extract comma-separated equipment names from data
function getEquipmentNamesList(data) {
  if (!data) return '';

  let names = [];

  if (Array.isArray(data.equipmentName)) {
    names = data.equipmentName;
  } else if (Array.isArray(data.equipmentNames)) {
    names = data.equipmentNames;
  } else if (Array.isArray(data.equipment)) {
    names = data.equipment
      .map((item) => {
        if (typeof item === 'string') return item;
        if (!item || typeof item !== 'object') return null;
        return item.name || item.equipmentName || null;
      })
      .filter(Boolean);
  } else if (data.equipmentName) {
    names = [data.equipmentName];
  }

  return names.join(', ');
}

function Pill({ color = '#ef4444', bg = '#fee2e2', children }) {
  return (
    <span style={{ background: bg, color, borderRadius: 999, padding: '4px 12px', fontSize: 14, fontWeight: 700 }}>
      {children}
    </span>
  );
}

function statusPill(status) {
  const base = {
    padding: '4px 12px',           // match priority pill sizing
    borderRadius: 999,              // same rounding as priority pill
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '1.1',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid transparent'
  };
  switch (status) {
    case 'Ongoing':
      return { ...base, backgroundColor: '#fef2f2', color: '#b91c1c', borderColor: '#fecdd3' };
    case 'Resolved':
      return { ...base, backgroundColor: '#ecfdf3', color: '#15803d', borderColor: '#bbf7d0' };
    default:
      return { ...base, backgroundColor: '#f3f4f6', color: '#4b5563', borderColor: '#e5e7eb' };
  }
}

const VIDEO_EXTENSIONS = /\.(mp4|mov|webm|ogg|avi|mkv|m4v)(\?|$)/i;

function isVideoUrl(url) {
  if (!url) return false;
  const src = typeof url === 'string' ? url : url.url || '';
  return VIDEO_EXTENSIONS.test(src);
}

export default function InspectionDetailsModal({ show, onClose, data, type = 'issue', onButtonClick, onStatusChange, buildingName = '' }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isRequestingQuote, setIsRequestingQuote] = useState(false);

  useEffect(() => {
    const attachments = type === 'issue' ? data?.photos : data?.attachments;
    const index = Number.isInteger(data?.initialImageIndex) ? data.initialImageIndex : 0;
    setCurrentImageIndex(Math.min(Math.max(0, index), Math.max(0, (attachments?.length || 1) - 1)));
  }, [data, show, type]);
  
  if (!data) return null;
  
  const images = type === 'issue' ? data.photos : data.attachments;
  const currentImage = images?.[currentImageIndex];
  const equipmentNamesList = getEquipmentNamesList(data);
  
  function nextImage() {
    setCurrentImageIndex((prev) => (prev + 1) % (images?.length || 1));
  }
  
  function prevImage() {
    setCurrentImageIndex((prev) => (prev - 1 + (images?.length || 1)) % (images?.length || 1));
  }

  async function handleRequestQuote() {
    if (!data?.id || isRequestingQuote) return;
    
    setIsRequestingQuote(true);
    
    try {
      // Get manager email from localStorage
      let managerEmail = '';
      const userStr = localStorage.getItem('managerUser');
      if (userStr) {
        try {
          const userObj = JSON.parse(userStr);
          managerEmail = userObj.email || '';
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      
      if (!managerEmail) {
        throw new Error('Manager email not found. Please log in again.');
      }
      
      // Generate the shareable issue link
      const issueLink = `${window.location.origin}${window.location.pathname}?issue=${data.id}`;
      
      // Call the API with issue title
      const issueTitle = data.title || data.issue_name || 'Issue';
      await requestQuote(managerEmail, issueLink, buildingName || 'Building', issueTitle);
      
      // Close modal and show success message
      onClose();
      if (onButtonClick) {
        onButtonClick('Quote requested - We will email it to you shortly');
      }
    } catch (error) {
      console.error('Failed to request quote:', error);
      if (onButtonClick) {
        onButtonClick(`Failed to request quote: ${error.message}`);
      }
    } finally {
      setIsRequestingQuote(false);
    }
  }

  async function handleShareReport() {
    setIsGeneratingReport(true);
    try {
      if (type === 'issue' && data?.id) {
        // For issues, generate and download Issue Report PDF
        await downloadIssueReportPdf(data.id, buildingName);
      } else {
        // For service history/maintenance, generate Maintenance Report PDF
        // Pass the data directly since we may not have an ID in Back4App
        // Extract URLs from attachments (they come as objects with {id, label, url})
        const attachmentUrls = (data.attachments || [])
          .map(att => (typeof att === 'string' ? att : att?.url))
          .filter(Boolean);
        
        // Extract file URLs (PDFs, etc.)
        const fileUrls = (data.files || [])
          .map(file => (typeof file === 'string' ? file : file?.url))
          .filter(Boolean);
        
        const visitData = {
          date: formatDate(data.date),
          technician: data.technician || 'N/A',
          type: data.type || 'Maintenance',
          findings: data.findings || '',
          details: data.details || '',
          equipmentNames: getEquipmentNamesList(data) || '',
          attachments: attachmentUrls,
          files: fileUrls
        };
        await downloadMaintenanceReportPdf(data.id || '', buildingName, visitData);
      }
      if (onButtonClick) {
        onButtonClick('Report downloaded successfully!');
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      if (onButtonClick) {
        onButtonClick('Failed to generate report. Please try again.');
      }
    } finally {
      setIsGeneratingReport(false);
    }
  }

  async function toggleStatus() {
    if (type !== 'issue' || !data?.id || isUpdatingStatus) return;
    const nextStatus = data.status === 'Resolved' ? 'Ongoing' : 'Resolved';
    try {
      setIsUpdatingStatus(true);
      await updateIssueStatus(data.id, nextStatus);
      // Optimistically update local modal view
      if (onStatusChange) {
        onStatusChange(data.id, nextStatus);
      }
    } catch (err) {
      console.error('Failed to update issue status', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size="lg" fullscreen="sm-down" className="manager-inspection-modal">
      <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Modal.Title>
          {type === 'issue' 
            ? `Issue Report${data.title ? ` — ${data.title}` : ''}`
            : 'Inspection Details'
          }
        </Modal.Title>
        <Button 
          variant="link" 
          aria-label="Close inspection details"
          onClick={onClose} 
          style={{ 
            textDecoration: 'none', 
            color: '#000', 
            fontSize: '1.5rem',
            padding: '0',
            border: 'none',
            background: 'none'
          }}
        >
          ×
        </Button>
      </Modal.Header>
      
      <Modal.Body style={{ padding: '1.5rem' }}>
        {/* Image Gallery */}
        {images && images.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="manager-inspection-gallery" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <Button variant="outline-secondary" aria-label="Previous inspection photo" onClick={prevImage} disabled={images.length <= 1}>
                ‹
              </Button>
              <div style={{ width: '100%', maxWidth: '500px', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {currentImage && (
                  isVideoUrl(currentImage) ? (
                    <video
                      key={currentImageIndex}
                      src={currentImage.url || currentImage}
                      controls
                      playsInline
                      style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px' }}
                    />
                  ) : (
                    <img 
                      src={currentImage.url || currentImage} 
                      alt="Inspection" 
                      style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px' }} 
                    />
                  )
                )}
              </div>
              <Button variant="outline-secondary" aria-label="Next inspection photo" onClick={nextImage} disabled={images.length <= 1}>
                ›
              </Button>
            </div>
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>
        )}

        {/* Data Fields */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          {type === 'issue' ? (
            <>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaExclamationTriangle />
                  </div>
                  Issue
                </div>
                <div style={{ fontWeight: '500' }}>{data.title}</div>
              </div>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaFlag />
                  </div>
                  Priority
                </div>
                <div style={{ fontWeight: '500' }}>
                  {data.priority === 'High' ? (
                    <Pill color="#b91c1c" bg="#fee2e2">High</Pill>
                  ) : data.priority === 'Medium' ? (
                    <Pill color="#ea580c" bg="#fed7aa">Medium</Pill>
                  ) : (
                    <Pill color="#ca8a04" bg="#fef3c7">Low</Pill>
                  )}
                </div>
              </div>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaExclamationTriangle />
                  </div>
                  Status
                </div>
                <div>
                  <button
                    onClick={toggleStatus}
                    disabled={isUpdatingStatus}
                    style={{
                      ...statusPill(data.status),
                      cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                      border: statusPill(data.status).border || statusPill(data.status).borderColor ? `1px solid ${statusPill(data.status).borderColor}` : '1px solid transparent',
                      backgroundColor: statusPill(data.status).backgroundColor
                    }}
                  >
                    {isUpdatingStatus ? 'Updating...' : data.status}
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaCalendarAlt />
                  </div>
                  Date Found
                </div>
                <div style={{ fontWeight: '500' }}>{formatDate(data.dateFound)}</div>
              </div>
            </>
          ) : (
            <>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaClipboardList />
                  </div>
                  Overview
                </div>
                <div style={{ fontWeight: '500' }}>{data.findings}</div>
              </div>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaCalendarAlt />
                  </div>
                  Date
                </div>
                <div style={{ fontWeight: '500' }}>{formatDate(data.date)}</div>
              </div>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaUser />
                  </div>
                  Technician
                </div>
                <div style={{ fontWeight: '500' }}>{data.technician}</div>
              </div>
              <div style={{ flex: 1, backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '2px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '8px',
                    color: '#9ca3af'
                  }}>
                    <FaWrench />
                  </div>
                  Type
                </div>
                <div style={{ fontWeight: '500' }}>{data.type}</div>
              </div>
            </>
          )}
        </div>

        {/* Details Field */}
        {data.details && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <div style={{ 
                width: '12px', 
                height: '12px', 
                backgroundColor: '#e5e7eb', 
                borderRadius: '2px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '8px',
                color: '#9ca3af'
              }}>
                <FaClipboardList />
              </div>
              Details
            </div>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '0.75rem', 
              borderRadius: '8px',
              maxHeight: '200px',
              overflowY: 'auto',
              lineHeight: '1.5', 
              color: '#374151',
              whiteSpace: 'pre-wrap'
            }}>
              {data.details}
            </div>
          </div>
        )}

        {/* Attached Files (PDFs, etc.) - Right after Details */}
        {data.files && data.files.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <div style={{ 
                width: '12px', 
                height: '12px', 
                backgroundColor: '#e5e7eb', 
                borderRadius: '2px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '8px',
                color: '#9ca3af'
              }}>
                <FaFilePdf />
              </div>
              Attached Files
            </div>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '0.75rem', 
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              {data.files.map((file, idx) => {
                const fileUrl = file.url || '';
                const isPdf = fileUrl.toLowerCase().endsWith('.pdf');
                return (
                  <a
                    key={file.id || idx}
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      textDecoration: 'none',
                      color: '#374151',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f7ff';
                      e.currentTarget.style.borderColor = '#2c59a2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    {isPdf ? (
                      <FaFilePdf style={{ color: '#dc2626', fontSize: '1.1rem', flexShrink: 0 }} />
                    ) : (
                      <FaFile style={{ color: '#6b7280', fontSize: '1.1rem', flexShrink: 0 }} />
                    )}
                    <span style={{ flex: 1 }}>
                      Attachment {idx + 1}
                    </span>
                    <FaExternalLinkAlt style={{ color: '#9ca3af', fontSize: '0.75rem', flexShrink: 0 }} />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Equipment(s) Serviced Field */}
        {equipmentNamesList && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <div style={{ 
                width: '12px', 
                height: '12px', 
                backgroundColor: '#e5e7eb', 
                borderRadius: '2px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '8px',
                color: '#9ca3af'
              }}>
                <FaWrench />
              </div>
              Equipment(s) Serviced
            </div>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '0.75rem', 
              borderRadius: '8px',
              maxHeight: '120px',
              overflowY: 'auto',
              lineHeight: '1.5', 
              color: '#374151' 
            }}>
              {equipmentNamesList}
            </div>
          </div>
        )}

        {/* Action Buttons or Resolved Message - Only show for issues, not service history */}
        {type === 'issue' && (
          data.status === 'Resolved' ? (
            <div>
              <div style={{ 
                textAlign: 'center', 
                padding: '1rem', 
                backgroundColor: '#d1fae5', 
                color: '#059669',
                borderRadius: '8px',
                fontWeight: '500',
                fontSize: '0.95rem',
                marginBottom: '1rem'
              }}>
                ✓ This issue has been resolved
              </div>
              <Button 
                variant="primary" 
                style={{ 
                  backgroundColor: '#2c59a2', 
                  borderColor: '#2c59a2', 
                  width: '100%',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  fontSize: '0.95rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1e40af';
                  e.target.style.borderColor = '#1e40af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#2c59a2';
                  e.target.style.borderColor = '#2c59a2';
                }}
                onClick={handleShareReport}
                disabled={isGeneratingReport}
              >
                <FaShare style={{ fontSize: '0.8rem' }} />
                {isGeneratingReport ? 'Generating...' : 'Share This Report'}
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button 
                variant="outline-primary" 
                style={{ 
                  borderColor: '#2c59a2', 
                  color: '#2c59a2', 
                  flex: 1,
                  padding: '0.75rem 1rem',
                  fontSize: '0.95rem',
                  ':hover': {
                    backgroundColor: 'transparent',
                    borderColor: '#2c59a2',
                    color: '#2c59a2'
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#2c59a2';
                  e.target.style.color = '#2c59a2';
                }}
                onClick={handleRequestQuote}
                disabled={isRequestingQuote}
              >
                {isRequestingQuote ? 'Sending...' : 'Request a Quote'}
              </Button>
              <Button 
                variant="primary" 
                style={{ 
                  backgroundColor: '#2c59a2', 
                  borderColor: '#2c59a2', 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  fontSize: '0.95rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1e40af';
                  e.target.style.borderColor = '#1e40af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#2c59a2';
                  e.target.style.borderColor = '#2c59a2';
                }}
                onClick={handleShareReport}
                disabled={isGeneratingReport}
              >
                <FaShare style={{ fontSize: '0.8rem' }} />
                {isGeneratingReport ? 'Generating...' : 'Share This Report'}
              </Button>
            </div>
          )
        )}
        
        {/* Share Report Button - Only show for service history (not issues) */}
        {type !== 'issue' && (
          <div style={{ 
            marginTop: '2rem', 
            paddingTop: '1rem', 
            borderTop: '1px solid #e5e7eb' 
          }}>
            <Button
              onClick={handleShareReport}
              disabled={isGeneratingReport}
              style={{
                backgroundColor: '#2C5AA2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e3a8a';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2C5AA2';
              }}
            >
              <FaShare style={{ fontSize: '0.8rem' }} />
              {isGeneratingReport ? 'Generating Report...' : 'Share This Report →'}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
